import React, {useContext, useEffect, useState} from "react";
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Slider,
  Typography,
} from "@mui/material";
import Select, {SelectChangeEvent} from "@mui/material/Select";
import {baseURL} from "../config";

import {authContext} from "../Context/Authentication/authContext";
import "./EconomicIndicators.scss";
import {styled} from "@mui/material/styles";
import {useDebounce} from "../SideMenu/useDebounce";
import Chart from "../Chart/Chart";
import axios from "axios";
import moment from "moment";
import {sortByDate} from '../util'
const indicators: any = [
  "Dom",
  "GDP",
  "Home sales",
  "MSP",
  "Mortgage Rate",
  "PPSF",
  "Population",
  "Unemployment rate"
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      width: 250,
    },
  },
};

const iOSBoxShadow =
  "0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.13),0 0 0 1px rgba(0,0,0,0.02)";

const IOSSlider = styled(Slider)(({theme}) => ({
  color: theme.palette.mode === "dark" ? "#f7b047" : "#f7b047",
  height: 2,
  padding: "15px 0",
  "& .MuiSlider-thumb": {
    height: 15,
    width: 15,
    backgroundColor: "#f7b047",
    boxShadow: iOSBoxShadow,
    "&:focus, &:hover, &.Mui-active": {
      boxShadow:
        "0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.3),0 0 0 1px rgba(0,0,0,0.02)",
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        boxShadow: iOSBoxShadow,
      },
    },
  },
  "& .MuiSlider-valueLabel": {
    fontSize: 12,
    fontWeight: "normal",
    top: -6,
    backgroundColor: "unset",
    color: theme.palette.text.primary,
    "&:before": {
      display: "none",
    },
    "& *": {
      background: "transparent",
      color: theme.palette.mode === "dark" ? "#fff" : "#f7b047",
    },
  },
  "& .MuiSlider-track": {
    color: "#f7b047",
  },
  "& .MuiSlider-rail": {
    opacity: 0.4,
    border: "1px solid #bfbfbf",
    backgroundColor: "#bfbfbf",
  },
  "& .MuiSlider-mark": {
    backgroundColor: "#bfbfbf",
    height: 8,
    width: 1,
    "&.MuiSlider-markActive": {
      opacity: 1,
      color: "rgb(255, 75, 75)",
      backgroundColor: "currentColor",
    },
  },
}));

const getYears = () => {
  let temp = [];
  for (let i = 2002; i < 2022; i++) {
    temp.push({
      value: i,
      label: i,
    });
  }
  return temp;
};

const ROLL_UPS_OPTIONS = ['Monthly', 'Quarterly', 'Yearly']

const EconomicIndicators = () => {
  const {state, dispatch}: any = useContext(authContext);

  const [years, setYears]: any = React.useState(getYears());
  const [year, setYear] = React.useState<number[]>([10, new Date().getFullYear()%100]);
  const [chartData, setChartData]: any = useState(null);
  const debounceYear = useDebounce(year, 300);
  const [rollUpOption, setRollUpOption]: any = useState(ROLL_UPS_OPTIONS[2]);

  useEffect(() => {
    if (JSON.stringify(state.filters.economicYears) !== JSON.stringify(year)) {
      dispatch({
        type: "APPLY_FILTER",
        payload: {
          ...state.filters,
          economicYears: year,
        },
      });
    }
  }, [debounceYear]);

  useEffect(() => {
    if (state.filters.indicators?.length) getEconomicIndicatorData();
    else setChartData([]);
  }, [
    state.filters.state,
    state.filters.indicators,
    state.filters.economicType,
    state.filters.economicYears,
    rollUpOption
  ]);

  const getEconomicIndicatorData = async () => {
    try {
      let start = state.filters.economicYears[0] + 2000;
      let end = state.filters.economicYears[1] + 2000;

      const indicators = JSON.stringify(state.filters.indicators)
        .replace(/[\[\]']+/g, "")
        .replaceAll('"', "'")
        .toLowerCase();
      const response = await axios.get(`${baseURL}geteconomicIndicator?state=${state.filters.state}&indicator=${indicators.split(" ").join("")}&startYear=${start}&endYear=${end}&indicatorType=${state.filters.economicType.toLowerCase() === "scaled" ? "scale": "growth"}&rollUp=${rollUpOption.toLowerCase()}`);

      const data = generateChartData(response.data?.cube);
      setChartData(data);
    } catch (error) {
      console.log("error", error);
    }
  };

  const findIndicatorValue = (data: any[], date: any) => {
    const indicator = data.find((item) => item.date === date);
    return indicator ? indicator.indicatorValue : undefined;
  }

  const generateChartData = (apiData: any) => {
    const keys = Object.keys(apiData);
    const data: any = [];
    const dates = new Set();
    keys.forEach((key) => {
      apiData[key].forEach((item: any) => {
        dates.add(moment(item.date).format('YYYY-MM-DD'));
      });
    });

    Array.from(dates).forEach((date) => {
      const values = { date };

      keys.forEach((key, index) => {
        // @ts-ignore
        values[`value${index + 1}`] = findIndicatorValue(apiData[key], date);
      });

      data.push(values);
    });
    return sortByDate(data)
  };

  const handleTypeChange = (event: SelectChangeEvent) => {
    let indicators = state?.filters?.indicators
    if (state?.filters?.indicators.length > 1)
      indicators = [indicators[0]]
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        economicType: event.target.value as string,
        indicators,
      },
    });
  };

  const handleRollUpChange = (event: SelectChangeEvent) => {
    setRollUpOption(event.target.value as string,)
  };

  const handleYearChange = (event: Event, newValue: number | number[]) => {
    setYear(newValue as number[]);
  };

  function valuetext(value: number) {
    return `${value}`;
  }

  function valueLabelFormat(value: number) {
    return value + 2000;
  }

  const handleIndicatorChange = (event: SelectChangeEvent<[]>) => {
    const {
      target: {value},
    } = event;
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        indicators: typeof value === "string" ? value.split(",") : value,
      },
    });
  };

  const handleDelete = (val: any) => {
    const temp = [...state?.filters?.indicators];
    dispatch({
      type: "APPLY_FILTER",
      payload: {
        ...state.filters,
        indicators: temp.filter((name) => name !== val),
      },
    });
  };

  const yAxisText = (val: string) => {
    console.log(val)
    if (val.length) {
      const value = val[0]
      if (value === 'PPSF')
        return 'PPSF ($)'
      else if (value === 'Population')
        return 'Population (In Thousands)'
      else if (value === 'MSP')
        return 'MSP ($)'
      else if(value === 'GDP'){
        return 'GDP (Millions in $)'
      }
      else
        return value
    } else
      return val
  }

  return (
    <div className="economic-container">
      <div className="economic-container__heading">
        Comparison of economic indicators with real estate indexes
      </div>
      <div className="economic-container__row">
        <div className="economic-container__col_1">
          <FormControl sx={{m: 1}} fullWidth variant="standard">
            <InputLabel shrink id="demo-simple-select-label">
              Select type
            </InputLabel>
            <Select
              value={state?.filters?.economicType}
              label="Select type"
              onChange={handleTypeChange}
              size="small"
            >
              <MenuItem value={"Scaled"}>Absolute</MenuItem>
              <MenuItem value={"Growth"}>Growth</MenuItem>
            </Select>
          </FormControl>
        </div>
        <div className="economic-container__col_1">
          <FormControl sx={{m: 1}} fullWidth variant="standard">
            <InputLabel shrink id="demo-simple-select-label">
              Roll Up
            </InputLabel>
            <Select
              value={rollUpOption}
              label="Select type"
              onChange={handleRollUpChange}
              size="small"
            >
              <MenuItem value={'Monthly'}>Monthly</MenuItem>
              <MenuItem value={'Quarterly'}>Quarterly</MenuItem>
              <MenuItem value={'Yearly'}>Yearly</MenuItem>
            </Select>
          </FormControl>
        </div>
        <div className="economic-container__col_2">
          <FormControl sx={{m: 1}} fullWidth variant="standard">
            <InputLabel id="demo-multiple-checkbox-label" shrink>
              Choose Indicators
            </InputLabel>
            <Select
              className='indicator-popup'
              size="small"
              labelId="demo-multiple-checkbox-label"
              id="demo-multiple-checkbox"
              multiple={state?.filters?.economicType === 'Growth'}
              value={state?.filters?.indicators}
              onChange={handleIndicatorChange}
              label="Select Zipcode"
              renderValue={(selected) => (
                <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                  {selected.map((value: any) => (
                    <Chip
                      key={value}
                      label={value}
                      color="primary"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                      }}
                      onDelete={() => handleDelete(value)}
                    />
                  ))}
                </Box>
              )}
              MenuProps={MenuProps}
            >
              {indicators.map((name: any) => (
                <MenuItem key={name} value={name}>
                  <ListItemText primary={name}/>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div className="economic-container__col_3">
          <FormControl sx={{m: 1}} fullWidth>
            <Typography gutterBottom>Choose range in years</Typography>
            <br></br>
            <IOSSlider
              valueLabelFormat={valueLabelFormat}
              getAriaValueText={valuetext}
              min={2}
              max={new Date().getFullYear()%100}
              value={year}
              step={1}
              onChange={handleYearChange}
              valueLabelDisplay="on"
              marks={years}
            />
          </FormControl>
        </div>
      </div>
      <div className="economic-container__chartContainer">
        {chartData === null ? (
          <div className="economic-container__chartContainer__isLoading">
            <CircularProgress/>
          </div>
        ) : chartData?.length ? (
          <Chart
            chartData={chartData}
            title={state.filters.economicType ? (state.filters.economicType === 'Scaled' ? yAxisText(state?.filters?.indicators) : `${state.filters.economicType} (%)`) : yAxisText(state?.filters?.indicators)}
            city={state.filters.indicators}
            zipcode={state.filters.indicators}
            indicators={state.filters.indicators}
            showInterval={state.filters.showInterval}
            zoomDate={chartData.length > 0 ? new Date(chartData[0].date): null}
            sidePanelHeading={state.filters.state}
            customTooltip
            yearlyDate={rollUpOption}

          />
        ) : (
          <div className="economic-container__chartContainer__noData">
            No Data Available
          </div>
        )}
      </div>
    </div>
  );
};

export default EconomicIndicators;